import { ShortcutPrinter } from './shortcuts-printer';
import {
  type DOMModifier,
  DOMModifierKeys,
  type OperatingSystem,
  type Shortcut,
  type ShortcutFlattened,
  type ShortcutModifier,
} from './shortcuts-types';

const domKeyToShortcutKeyLookup: Record<string, string> = {
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
};

export class ShortcutKeyMap<T extends string> {
  private sequences: Record<string, ShortcutFlattened>;
  private printer: ShortcutPrinter<T>;

  static fromDOMModifier(
    os: OperatingSystem,
    domModifier: DOMModifier
  ): ShortcutModifier {
    switch (domModifier) {
      case 'Meta': {
        if (os === 'mac') {
          return 'Command';
        } else {
          return 'Meta';
        }
      }
      default: {
        return domModifier;
      }
    }
  }

  static fromDOMKey(domKey: string): string {
    return domKeyToShortcutKeyLookup[domKey] ?? domKey;
  }

  static isDOMKeyModifier(domKey: string): domKey is DOMModifier {
    // DOMModifierKeys is of type 'Meta' | 'Shift' | 'Control' | 'Alt' so we need to cast
    // to a string[] first
    return (DOMModifierKeys as readonly string[]).includes(domKey);
  }

  constructor(private os: OperatingSystem) {
    this.sequences = {};
    this.printer = new ShortcutPrinter<T>();
  }

  add(shortcut: Shortcut<T>) {
    const shortcutType = shortcut.type || 'combination';

    const shortcutSequence = this.printer.printKeyMap(this.os, shortcut);
    const shortcutDefinition: ShortcutFlattened = {
      id: shortcut.id,
      title: shortcut.title,
      type: shortcutType,
      sequence: shortcutSequence,
      preventDefault: shortcut.preventDefault,
    };

    if (shortcutType === 'combination') {
      this.sequences[this.formatSequence(shortcutSequence)] = shortcutDefinition;
    } else {
      for (const char of shortcutSequence) {
        this.sequences[char] = {
          ...shortcutDefinition,
          type: 'option',
          sequence: [char],
        };
      }
    }
  }

  get(sequence: string[]) {
    const shortcutSequence = this.formatSequence(sequence);
    const shortcut = this.sequences[shortcutSequence];
    if (shortcut) {
      return { ...shortcut, sequence: shortcutSequence };
    }
    return undefined;
  }

  getAll() {
    return this.sequences;
  }

  private formatSequence(sequence: string[]) {
    return sequence.join('+');
  }
}
